import { AlertType, Field, Icon, Template, TemplateFolder } from 'src/app/hickory-api';
import { AlertModelService } from '../alert-model.service';
import { AlertFields, AlertFieldsHelper } from './alert-fields-helper';
import { AlertMessageHelper } from './alert-message-helper';
import { AlertRecipientsHelper } from './alert-recipients-helper';
import { AlertDefaultsHelper } from './alert-defaults-helper';

export class AlertTemplateHelper {
  static initTemplate(template?: Template, alertType?: AlertType): Template {
    alertType = alertType ||
      (template?.alertType as unknown as AlertType) ||
      template?.alertSettings?.alertType ||
      AlertType.Default;
    if (!template) template = { };
    if (!template.icon) {
      const iconKey = alertType === AlertType.Poll ? 'poll' : 'announcement';
      template.icon = { key: iconKey, color: '#000000', type: Icon.TypeEnum.Material };
    }
    if (!template.action) template.action = Template.ActionEnum.Edit;
    if (!template.actions) template.actions = ['create', 'read', 'update', 'delete']; // init with all actions to prevent UI 'flashing'
    if (!template.pinned) template.pinned = template.action !== Template.ActionEnum.Edit;
    if (!template.alertSettings) template.alertSettings = {};
    if (!template.metadata) template.metadata = { tags: {} };
    AlertDefaultsHelper.initDefaults(template.alertSettings, alertType);
    return template;
  }

  static getAlertType(template: Template): AlertType {
    let alertType = template?.alertType as unknown as AlertType;
    if (!alertType) alertType = template?.alertSettings?.alertType;
    return alertType || AlertType.Default;
  }

  /**
   * Applyies "magical" rules when a new field is added that affects other fields already in the list
   */
  static applyFieldAdded(added: string, fields: Field[], service: AlertModelService): Field[] {
    if (!fields) return fields;
    if (!added) return fields;

    switch (added) {
      case AlertFields.ATTACHMENTS:
        this.applyFieldRemoved(service, AlertFields.PUBLIC_ATTACHMENTS);
        return fields.filter(f => f.name !== AlertFields.PUBLIC_ATTACHMENTS && f.name !== AlertFields.HIDE_ATTACHMENTS);
      case AlertFields.PUBLIC_ATTACHMENTS: 
        this.applyFieldRemoved(service, AlertFields.ATTACHMENTS);
        return fields.filter(f => f.name !== AlertFields.ATTACHMENTS && f.name !== AlertFields.HIDE_ATTACHMENTS);
      case AlertFields.HIDE_ATTACHMENTS: 
        this.applyFieldRemoved(service, AlertFields.ATTACHMENTS);
        this.applyFieldRemoved(service, AlertFields.PUBLIC_ATTACHMENTS);
        return fields.filter(f => f.name !== AlertFields.ATTACHMENTS && f.name !== AlertFields.PUBLIC_ATTACHMENTS);
      case AlertFields.INCIDENT_V1:
        this.applyFieldRemoved(service, AlertFields.INCIDENT_V2);
        this.applyFieldRemoved(service, AlertFields.PRIORITY);
        return fields.filter(f => f.name !== AlertFields.INCIDENT_V2 && f.name !== AlertFields.PRIORITY);
      case AlertFields.INCIDENT_V2:
        this.applyFieldRemoved(service, AlertFields.INCIDENT_V1);
        this.applyFieldRemoved(service, AlertFields.PRIORITY);
        return fields.filter(f => f.name !== AlertFields.INCIDENT_V1 && f.name !== AlertFields.PRIORITY);
      case AlertFields.PRIORITY:
        this.applyFieldRemoved(service, AlertFields.INCIDENT_V1);
        this.applyFieldRemoved(service, AlertFields.INCIDENT_V2);
        return fields.filter(f => f.name !== AlertFields.INCIDENT_V1 && f.name !== AlertFields.INCIDENT_V2);
      default: {
        return fields;
      }
    }
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  static applyFieldRemoved(service: AlertModelService, fieldName: string) {
    if (!service) return;
    const message = service.alertMessage;
    switch (fieldName) {
      case AlertFields.ATTACHMENTS:
        service.state.files = [];
        break;
      case AlertFields.CHANNELS:
        // ROAD-7869 -- use "vanilla" defaults here to avoid potentially empty channels on the template
        service.alertSettings.channels = service.vanillaDefaults?.channels ?? service.alertDefaults?.channels ?? {};
        break;
      case AlertFields.FACILITIES:
        if (service.alertLocation) service.alertLocation.facilities = undefined;
        if (service.alertFacility) service.alertFacility.facilities = undefined;
        if (service.alertFacility) service.alertFacility.areas = undefined;
        if (service.state?.mapFacilities) service.state.mapFacilities = undefined;
        break;
      case AlertFields.GEO_FENCING:
        service.state.mapSelection = undefined;
        service.alertRecipients.geofences = undefined;
        service.alertRecipients.featureLayers = undefined;
        break;
      case AlertFields.INCIDENT_V1:
        if (message) {
          message.incident = undefined;
          message.incidentId = undefined;
        }
        break;
      case AlertFields.INCIDENT_V2:
        if (message) message.incidentEvent = undefined;
        break;
      case AlertFields.LOCATION:
        if (service.alertLocation) service.alertLocation.point = undefined;
        service.state.mapLocation = undefined;
        break;
      case AlertFields.MESSAGE:
        if (message) {
          message.content = undefined;
          message.basic = AlertMessageHelper.initBasicMessage()
        }
        break;
      case AlertFields.POLL_OPTIONS:
        if (service.alertPoll) service.alertPoll.options = [];
        break;
      case AlertFields.POLL_QUESTION:
        if (service.alertPoll) {
          service.alertPoll.question = undefined;
          service.alertPoll.options = [];
        }
        break;
      case AlertFields.PUBLIC_ATTACHMENTS:
        if (message) message.files = undefined;
        break;
      case AlertFields.RECIPIENTS:
        service.alertSettings.recipients = AlertRecipientsHelper.initRecipients();
        break;
      case AlertFields.SENDER:
        service.alertTemplate.brandId = undefined;
        service.alertSettings.brandId = undefined;
        service.alertSettings.sender = {};
        break;
      case AlertFields.SURVEY_SENDER:
        delete service.alertMessage?.survey?.sender;
        break;
      case AlertFields.SURVEY_RECIPIENT:
        delete service.alertMessage?.survey?.recipient;
        break;
      case AlertFields.SEVERITY_ASSESSMENT:
        service.state.questionnaire = undefined;
        if (message) message.severityAssessment = undefined;
        break;
      case AlertFields.SOCIAL: {
          const defaults = service.alertDefaults?.channels || {}
          if (service.alertChannels?.facebook) service.alertChannels.facebook = defaults.facebook || {};
          if (service.alertChannels?.twitter) service.alertChannels.twitter = defaults.twitter || {};
          if (service.alertChannels?.nextdoor) service.alertChannels.nextdoor = defaults.nextdoor || {};
          break;
        }
    }
  }

  static isAutofillDisabled(_: AlertModelService, fieldName: string): boolean {
    return fieldName !== AlertFields.LOCATION;
  }

  static isReadonlyDisabled(service: AlertModelService, fieldName: string): boolean {
    if (service.isPanicTemplate) {
      return ![AlertFields.MESSAGE, AlertFields.SUBJECT, AlertFields.ATTACHMENTS].includes(fieldName);
    } else {
      return fieldName === AlertFields.SURVEY_SENDER;
    }
  }

  static isInvisibleDisabled(service: AlertModelService, fieldName: string): boolean {
    return service.isPanicTemplate || fieldName === AlertFields.SURVEY_SENDER;
  }

  static isRequiredDisabled(service: AlertModelService, fieldName: string): boolean {
    const fields = AlertFieldsHelper.fieldsAlwaysRequired(service.alertType, service.useAlertPriorityV2);
    if (service.isPanicTemplate) {
      // disallow requiring attachments for "panic" templates
      fields.push(AlertFields.ATTACHMENTS);
    }
    
    return fields.includes(fieldName);
  }

  static isOptionVisible(_: AlertModelService, fieldName: string, option: 'autofill' | 'readonly' | 'invisible' | 'required'): boolean {
    switch (fieldName) {
      case AlertFields.TEMPLATE: return false;

      case AlertFields.HIDE_ATTACHMENTS: return false;

      case AlertFields.ASSET_LIST_IMPORT:
      case AlertFields.ASSET_SELECTION:
      case AlertFields.ASSOCIATION_RECIPIENTS: return false;

      case AlertFields.SENDER:
      case AlertFields.DISABLE_REPLY_ALL: return option !== 'required';

      default: return true;
    }
  }

  static sortItems(items: TemplateFolder[] | Template[]) {
    items?.sort((a: TemplateFolder | Template, b: TemplateFolder | Template) => {
      const aName: string = a?.name;
      if (!aName) return 1;
      const bName: string = b?.name;
      if (!bName) return -1;
      return aName.localeCompare(bName, undefined, { sensitivity: 'base' });
    });

    items?.forEach((child: TemplateFolder | Template) => {
      this.sortItems(child['folders']);
      this.sortItems(child['templates']);
    });
  }

  static hasTemplateUpdateAction(template: Template): boolean {
    // ROAD-7657 - Use access actions to hide save buttons on the edit form
    return template?.actions?.includes("update");
  }

  static hasTemplateCreateAction(template: Template): boolean {
    // ROAD-7657 - Use access actions to hide save buttons on the edit form
    return template?.actions?.includes("create");
  }

  static fixMisconfiguredTemplateIcon(template: Template) {
    if (!template?.alertSettings || template?.icon?.type !== Icon.TypeEnum.Material) return;
    const alertKey = 'announcement';
    const pollKey = 'poll';
    const alertType = template.alertType ?? template.alertSettings?.alertType;
    if (alertType === AlertType.Default && template?.icon.key === pollKey) template.icon.key = alertKey;
    if (alertType === AlertType.Poll && template?.icon.key === alertKey) template.icon.key = pollKey;
  }
}
